import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { X, Trash2 } from 'lucide-react';

interface LogEntry {
  id: string;
  timestamp: string;
  level: 'info' | 'warning' | 'error' | 'debug';
  message: string;
  data?: any;
}

interface FrontendLoggerProps {
  isVisible?: boolean;
  onClose?: () => void;
  maxLogs?: number;
}

class FrontendLoggerService {
  private static instance: FrontendLoggerService;
  private logs: LogEntry[] = [];
  private listeners: ((logs: LogEntry[]) => void)[] = [];

  static getInstance(): FrontendLoggerService {
    if (!FrontendLoggerService.instance) {
      FrontendLoggerService.instance = new FrontendLoggerService();
    }
    return FrontendLoggerService.instance;
  }

  log(level: 'info' | 'warning' | 'error' | 'debug', message: string, data?: any) {
    const entry: LogEntry = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      timestamp: new Date().toLocaleTimeString(),
      level,
      message,
      data
    };

    this.logs.unshift(entry);
    
    // Keep only last 50 logs
    if (this.logs.length > 50) {
      this.logs = this.logs.slice(0, 50);
    }

    this.notifyListeners();
  }

  getLogs(): LogEntry[] {
    return this.logs;
  }

  clearLogs() {
    this.logs = [];
    this.notifyListeners();
  }

  subscribe(listener: (logs: LogEntry[]) => void) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener(this.logs));
  }
}

export const frontendLogger = FrontendLoggerService.getInstance();

const FrontendLogger: React.FC<FrontendLoggerProps> = ({ 
  isVisible = true, 
  onClose,
  maxLogs = 20 
}) => {
  const [logs, setLogs] = useState<LogEntry[]>([]);

  useEffect(() => {
    const unsubscribe = frontendLogger.subscribe(setLogs);
    return unsubscribe;
  }, []);

  if (!isVisible) return null;

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'error': return 'text-red-600 bg-red-50 border-red-200';
      case 'warning': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'debug': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  return (
    <Card className="fixed bottom-4 right-4 w-96 max-h-96 z-50 shadow-lg">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">Debug Logs</CardTitle>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => frontendLogger.clearLogs()}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
            {onClose && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <ScrollArea className="h-64">
          <div className="space-y-1">
            {logs.slice(0, maxLogs).map((log) => (
              <div
                key={log.id}
                className={`p-2 rounded text-xs border ${getLevelColor(log.level)}`}
              >
                <div className="flex justify-between items-start mb-1">
                  <span className="font-mono font-bold uppercase">{log.level}</span>
                  <span className="text-xs opacity-70">{log.timestamp}</span>
                </div>
                <div className="font-mono">{log.message}</div>
                {log.data && (
                  <pre className="mt-1 text-xs opacity-80 overflow-auto">
                    {JSON.stringify(log.data, null, 2)}
                  </pre>
                )}
              </div>
            ))}
            {logs.length === 0 && (
              <div className="text-center text-gray-500 py-4">
                No logs yet...
              </div>
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default FrontendLogger;
